import {makeAutoObservable} from "mobx";
import type {FunctionTemplate} from "../@types/Types";
import CsInstance from "./CsInstance";

export const THEATER_RENDER_WIDTH = 800;
export const THEATER_RENDER_HEIGHT = 600;

/**
 * MobX store for state that is specific to PixiJS; that is, the theater
 * renderer.  Contains the state of the theater internals, such as the scale
 * of the theater.
 */
class UserInputStore {
	private _isTheaterFocused = false;
	private _isEditorFocused = false;
	private _mouseX = 0;
	private _mouseY = 0;
	private _mouseInBounds = false;
	private _isMouseDown = false;
	private _fps = 0;
	private _theaterScale = 1;

	// Map from key code to key name. Key is only present if the key is pressed.
	private _pressedEventCodeToName = new Map<string, string>();
	private _spriteUuid = "";

	// Pixi Interactive Variables
	private _functionChosen = {
		name: "",
		parameterTypes: [],
		returnType: "",
	} as FunctionTemplate;
	private _waitingForInvokeFunction = false;

	private _csInstance: CsInstance;

	constructor(props: {csInstance: CsInstance}) {
		this._csInstance = props.csInstance;
		makeAutoObservable(this);
	}

	/**
	 * Handle mouse clicks on the editor to determine if the editor is focused.
	 * @param e The mouse event.
	 */
	handleFocusEvent = (e: MouseEvent) => {
		// Handle focusing the editor when clicking on it and unfocusing it when clicking outside of it
		if (document.getElementById("editor")?.contains(e.target as Node)) {
			// Make sure to go through setters
			this.isTheaterFocused = false;
			this.isEditorFocused = true;
		} else {
			this.isEditorFocused = false;
		}

		// Handle closing the context menu when clicking outside of it
		if (!document.getElementById("contextMenu")?.contains(e.target as Node)) {
			this._csInstance.uiStore.showContextMenu = false;
		}
	};

	keydownListener = (event: KeyboardEvent) => {
		if (this._isTheaterFocused) {
			this.pressedEventCodeToName.set(event.code, event.key);
		}
	};

	keyupListener = (event: KeyboardEvent) => {
		// See https://stackoverflow.com/questions/11818637
		// Deselect all keys when the meta key is released as a best effort
		if (event.code.startsWith("Meta") /* MetaLeft | MetaRight */) {
			this.pressedEventCodeToName.clear();
		} else {
			if (this._isTheaterFocused) {
				this.pressedEventCodeToName.delete(event.code);
			}
		}
	};

	blurListener = () => {
		this.pressedEventCodeToName.clear();
	};

	addKeyboardListeners() {
		document.addEventListener("keydown", this.keydownListener);
		document.addEventListener("keyup", this.keyupListener);
		window.addEventListener("blur", this.blurListener);
	}

	removeKeyboardListeners() {
		document.removeEventListener("keydown", this.keydownListener);
		document.removeEventListener("keyup", this.keyupListener);
		window.removeEventListener("blur", this.blurListener);
	}

	/**
	 * Return a list of keys pressed as an array of the form { code: string, key: string } -- as expected by the
	 * Java API.
	 */
	getKeysPressed() {
		return [...this._pressedEventCodeToName.entries()].map(([code, key]) => {
			return {key: key, code: code};
		});
	}

	updateTheaterScaleFromContainerDivWidth(containerDivWidth: number) {
		const theaterScale = containerDivWidth / THEATER_RENDER_WIDTH;
		if (this._theaterScale !== theaterScale) {
			this._theaterScale = theaterScale;
		}
	}

	// autogen getters and setters for MobX actions

	get isTheaterFocused() {
		return this._isTheaterFocused;
	}

	set isTheaterFocused(value) {
		this._isTheaterFocused = value;

		this._csInstance.codeStore.codeMirrorStore.updateEditability();
	}

	get isEditorFocused() {
		return this._isEditorFocused;
	}

	set isEditorFocused(value) {
		this._isEditorFocused = value;

		const view = this._csInstance.codeStore.codeMirrorStore.codemirrorView;
		if (view && value) {
			// Focus the editor
			view.focus();
		}
	}

	get mouseX() {
		return this._mouseX;
	}

	set mouseX(value) {
		this._mouseX = value;
	}

	get mouseY() {
		return this._mouseY;
	}

	set mouseY(value) {
		this._mouseY = value;
	}

	get mouseInBounds() {
		return this._mouseInBounds;
	}

	set mouseInBounds(value) {
		this._mouseInBounds = value;
	}

	get isMouseDown() {
		return this._isMouseDown;
	}

	set isMouseDown(value) {
		this._isMouseDown = value;
	}

	get fps() {
		return this._fps;
	}

	set fps(value) {
		this._fps = value;
	}

	get pressedEventCodeToName() {
		return this._pressedEventCodeToName;
	}

	set pressedEventCodeToName(value) {
		this._pressedEventCodeToName = value;
	}

	get spriteUuid() {
		return this._spriteUuid;
	}

	set spriteUuid(value) {
		this._spriteUuid = value;
	}

	get functionChosen() {
		return this._functionChosen;
	}

	set functionChosen(value) {
		this._functionChosen = value;
	}

	get waitingForInvokeFunction() {
		return this._waitingForInvokeFunction;
	}

	set waitingForInvokeFunction(value) {
		this._waitingForInvokeFunction = value;
	}

	get theaterScale() {
		return this._theaterScale;
	}

	set theaterScale(value) {
		this._theaterScale = value;
	}
}

export default UserInputStore;
